<template>
  <div class="pageRoot linkListPage aiMemoryReviewPage">
    <div ref="pageCard" class="pageShell">
      <div ref="pageCardMain" class="pageShell__main">
        <div ref="scrollArea" class="pageShell__scroll">
          <div class="listWorkspace">
            <section ref="filterAnchor" class="filterCard filterAnchor">
              <ai-role-memory-review-list-filter-panel
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section ref="tableZone" class="tableCard auditChatZone">
              <div v-if="loading" class="tableSkeletonWrap">
                <el-skeleton :rows="10" animated />
              </div>

              <div v-else-if="listData.length" class="auditChatList">
                <article
                  v-for="item in listData"
                  :key="item.id || item.key"
                  class="auditChatSession"
                >
                  <header class="auditChatSession__toolbar">
                    <div class="auditChatSession__meta">
                      <div class="auditChatSession__metaItem">
                        <span class="auditChatSession__metaLabel">UID</span>
                        <span class="auditChatSession__metaValue">{{ item.uid }}</span>
                      </div>
                      <div class="auditChatSession__metaItem">
                        <span class="auditChatSession__metaLabel">RoleKey</span>
                        <span class="auditChatSession__metaValue">{{ item.roleKey }}</span>
                      </div>
                      <div class="auditChatSession__metaItem">
                        <span class="auditChatSession__metaLabel">状态</span>
                        <span
                          class="statusPill"
                          :class="'statusPill--' + statusPillVariant(item.status)"
                        >
                          <span class="statusPill__dot" aria-hidden="true" />
                          {{ statusLabel(item.status) }}
                        </span>
                      </div>
                      <div class="auditChatSession__metaItem">
                        <span class="auditChatSession__metaLabel">更新时间</span>
                        <span class="auditChatSession__metaValue">{{
                          item.updateTime || '—'
                        }}</span>
                      </div>
                    </div>
                    <div class="auditChatSession__actions">
                      <el-button
                        size="small"
                        class="rowActionBtn rowActionBtn--ghost"
                        @click="resolveStatus(item.id)"
                      >
                        通过
                      </el-button>
                      <el-button
                        size="small"
                        class="rowActionBtn rowActionBtn--ghost rowActionBtn--danger"
                        @click="rejectStatus(item.id)"
                      >
                        驳回
                      </el-button>
                    </div>
                  </header>

                  <div class="auditChatSession__messages">
                    <div
                      v-for="(content, contentIndex) in item.translateList || []"
                      :key="contentIndex"
                      class="auditChatBubble"
                      :class="
                        content.type === 'user'
                          ? 'auditChatBubble--user'
                          : 'auditChatBubble--role'
                      "
                    >
                      <img
                        :src="chatAvatar(content.type)"
                        alt=""
                        class="auditChatBubble__avatar"
                        width="44"
                        height="44"
                      />
                      <div class="auditChatBubble__body">
                        <div class="auditChatBubble__content">{{ content.content }}</div>
                        <div
                          v-if="originalContent(item, contentIndex)"
                          class="auditChatBubble__original"
                        >
                          {{ originalContent(item, contentIndex) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>

              <div v-else class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-chat-dot-round" /></div>
                <p class="tableEmpty__title">暂无聊天审核记录</p>
                <p class="tableEmpty__desc">调整筛选条件后重新查询。</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第
          {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[10, 20, 30]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAiRoleMemoryReviewList,
  updateAiRoleMemoryReviewStatus,
} from '@/api/aiRoleMemoryReview'
import AiRoleMemoryReviewListFilterPanel from './AiRoleMemoryReviewListFilterPanel.vue'
import {
  CHAT_AVATAR,
  memoryReviewStatusLabel,
  memoryReviewStatusPillVariant,
} from '../aiAuditStatus'

export default {
  name: 'AiRoleMemoryReview',
  components: {
    AiRoleMemoryReviewListFilterPanel,
  },
  data() {
    return {
      listData: [],
      total: 0,
      uid: '',
      status: '',
      currentPage: 1,
      currentPageSize: 10,
      loading: false,
    }
  },
  computed: {
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.uid, this.status].filter(
        (value) => value !== '' && value !== null && value !== undefined
      ).length
    },
    filterPanelProps() {
      return {
        uid: this.uid,
        status: this.status,
        activeFilterCount: this.activeFilterCount,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:uid': (value) => {
          this.uid = value
        },
        'update:status': (value) => {
          this.status = value
          this.search()
        },
      }
    },
  },
  created() {
    this.getListData()
  },
  methods: {
    statusLabel: memoryReviewStatusLabel,
    statusPillVariant: memoryReviewStatusPillVariant,
    chatAvatar(type) {
      return type === 'user' ? CHAT_AVATAR.user : CHAT_AVATAR.role
    },
    originalContent(item, index) {
      const list = item.originalList
      if (!Array.isArray(list) || !list[index]) return ''
      return list[index].content || ''
    },
    getListData() {
      this.loading = true
      getAiRoleMemoryReviewList({
        pageNum: this.currentPage,
        pageSize: this.currentPageSize,
        uid: this.uid,
        status: this.status,
      })
        .then((res) => {
          if (res.success) {
            this.listData = res.data || []
            this.total = res.total
          }
        })
        .catch((err) => {
          console.error(err)
        })
        .finally(() => {
          this.loading = false
        })
    },
    search() {
      this.currentPage = 1
      this.getListData()
    },
    resetFilters() {
      this.uid = ''
      this.status = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => {
        this.getListData()
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getListData()
    },
    resolveStatus(id) {
      updateAiRoleMemoryReviewStatus({ id, status: 1 })
        .then((res) => {
          if (res.success) {
            this.$message.success('修改状态成功')
            this.getListData()
          }
        })
        .catch((err) => {
          this.$message.error(String(err))
        })
    },
    rejectStatus(id) {
      this.$confirm('确认驳回该条聊天记录？', '驳回确认', {
        confirmButtonText: '驳回',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() =>
          updateAiRoleMemoryReviewStatus({ id, status: 2 }).then((res) => {
            if (res.success) {
              this.$message.success('修改状态成功')
              this.getListData()
            }
          })
        )
        .catch(() => {})
    },
  },
}
</script>
